import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Check as CheckIcon,
  CloudUpload as UploadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { conciergeApi } from '../services/concierge-api-client';

// Must match services/chatbot_builder/constants.py MAX_UPLOAD_BYTES — the signed
// upload URL bakes this range into its signature, so the PUT must echo it.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

// --- Types ---

interface ConciergeConfig {
  persona_name: string;
  greeting: string;
  tone: string;
  formality: string;
  restrictions: string[];
  fallback_message: string;
  travel_features_enabled: boolean;
}

interface ConciergeInstance {
  instance_id: string;
  name: string;
  description: string;
  status: string;
  config: ConciergeConfig;
  channel: {
    type: string;
    phone_number: string;
    bird_channel_id: string;
    status: string;
  };
  stats: {
    total_messages: number;
    total_documents: number;
    total_chunks: number;
  };
}

interface UploadRecord {
  upload_id: string;
  filename: string;
  mime_type: string;
  file_size_bytes: number;
  status: 'pending' | 'processing' | 'ready' | 'error';
  chunk_count: number;
  error_message?: string;
  created_at?: string;
}

// --- Helpers ---

const TONE_OPTIONS = [
  { value: 'warm_friendly', label: 'Warm & Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
];

function StatusChip({ status }: { status: string }) {
  const colorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
    ready: 'success',
    processing: 'warning',
    pending: 'warning',
    error: 'error',
  };
  const iconMap: Record<string, React.ReactNode> = {
    ready: <CheckIcon sx={{ fontSize: 14 }} />,
    processing: <CircularProgress size={12} />,
    pending: <PendingIcon sx={{ fontSize: 14 }} />,
    error: <ErrorIcon sx={{ fontSize: 14 }} />,
  };
  return (
    <Chip
      icon={iconMap[status] as any}
      label={status}
      size="small"
      color={colorMap[status] ?? 'default'}
    />
  );
}

// --- Main component ---

export default function ConciergeDetail() {
  const { id: instanceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [restrictionInput, setRestrictionInput] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // --- Queries ---

  const { data: instance, isLoading } = useQuery<ConciergeInstance>(
    ['concierge', instanceId],
    () => conciergeApi.getConcierge(instanceId!),
    { staleTime: 30_000, enabled: !!instanceId }
  );

  const { data: uploads = [], refetch: refetchUploads } = useQuery<UploadRecord[]>(
    ['concierge-uploads', instanceId],
    () => conciergeApi.listUploads(instanceId!),
    { staleTime: 10_000, enabled: tab === 0 && !!instanceId }
  );

  // Config edit state (mirrors instance.config)
  const [config, setConfig] = useState<ConciergeConfig | null>(null);
  React.useEffect(() => {
    if (instance?.config && !config) {
      setConfig({ ...instance.config });
    }
  }, [instance]);

  // --- Mutations ---

  const saveMutation = useMutation(
    async () => {
      await conciergeApi.updateConfig(instanceId!, config!);
    },
    {
      onSuccess: () => {
        setSaveSuccess(true);
        queryClient.invalidateQueries(['concierge', instanceId]);
        setTimeout(() => setSaveSuccess(false), 3000);
      },
    }
  );

  const deleteUploadMutation = useMutation(
    async (uploadId: string) => {
      await conciergeApi.deleteUpload(instanceId!, uploadId);
    },
    { onSuccess: () => refetchUploads() }
  );

  const deleteConciergeMutation = useMutation(
    async () => {
      await conciergeApi.deleteConcierge(instanceId!);
    },
    { onSuccess: () => navigate('/concierges') }
  );

  // --- Upload handler ---

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (!selected.length) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadError(null);

    for (const file of selected) {
      try {
        const data = await conciergeApi.getUploadUrl(instanceId!, {
          filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          file_size_bytes: file.size,
        });
        const putRes = await fetch(data.upload_url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            // Required: the signed URL bakes this range into its signature; without
            // it the PUT 403s (SignatureDoesNotMatch).
            'x-goog-content-length-range': `0,${MAX_UPLOAD_BYTES}`,
          },
        });
        // fetch() resolves on HTTP errors — check res.ok or a failed upload
        // looks successful and the row stays 'pending' forever.
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      } catch (err: any) {
        console.error('Upload failed', err);
        setUploadError(`Could not upload ${file.name}: ${err?.message || 'unknown error'}`);
      }
    }
    refetchUploads();
  };

  // --- Test chat ---

  const handleSendTest = async () => {
    if (!testMessage.trim()) return;
    setTestLoading(true);
    setTestResponse('');
    try {
      // Test chat goes to the controller service, not the concierge builder API
      // TODO: wire this to the actual controller /chat endpoint once deployed
      setTestResponse('Test chat not yet connected to controller service. Deploy to staging to test end-to-end.');
    } catch (err: any) {
      setTestResponse(`Error: ${err.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  // --- Render helpers ---

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!instance) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Concierge not found.</Alert>
      </Box>
    );
  }

  const phoneNumber = instance.channel?.phone_number;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/concierges')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {instance.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {phoneNumber} · {instance.stats?.total_documents ?? 0} docs ·{' '}
            {instance.stats?.total_messages ?? 0} messages
          </Typography>
        </Box>
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Knowledge Base" />
        <Tab label="Settings" />
        <Tab label="Share" />
        <Tab label="Test" />
      </Tabs>

      {/* Tab 0: Knowledge Base */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Uploaded Documents</Typography>
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.jpg,.jpeg,.png,.gif,.webp,.txt,.md"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload More
              </Button>
            </Box>
          </Box>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
              {uploadError}
            </Alert>
          )}

          {uploads.length === 0 && (
            <Alert severity="info">
              No documents yet. Upload files to give your concierge a knowledge base.
            </Alert>
          )}

          {uploads.map((upload) => (
            <Paper
              key={upload.upload_id}
              variant="outlined"
              sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {upload.filename}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <StatusChip status={upload.status} />
                  {upload.status === 'processing' && (
                    <LinearProgress sx={{ flex: 1, height: 3 }} />
                  )}
                  {upload.chunk_count > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {upload.chunk_count} chunks indexed
                    </Typography>
                  )}
                  {upload.error_message && (
                    <Typography variant="caption" color="error" noWrap>
                      {upload.error_message}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Tooltip title="Delete document">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteUploadMutation.mutate(upload.upload_id)}
                  disabled={deleteUploadMutation.isLoading}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Box>
      )}

      {/* Tab 1: Settings */}
      {tab === 1 && config && (
        <Box sx={{ maxWidth: 600 }}>
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Settings saved — takes effect on the next message.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Persona Name"
              value={config.persona_name}
              onChange={(e) => setConfig({ ...config, persona_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Greeting Message"
              value={config.greeting}
              onChange={(e) => setConfig({ ...config, greeting: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Select
              value={config.tone}
              onChange={(e) => setConfig({ ...config, tone: e.target.value })}
              fullWidth
            >
              {TONE_OPTIONS.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
            <Box>
              <Typography variant="body2" gutterBottom>
                Topic Restrictions
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  value={restrictionInput}
                  onChange={(e) => setRestrictionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && restrictionInput.trim()) {
                      setConfig({
                        ...config,
                        restrictions: [...config.restrictions, restrictionInput.trim()],
                      });
                      setRestrictionInput('');
                    }
                  }}
                  placeholder="Add restriction and press Enter"
                  sx={{ flex: 1 }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {config.restrictions.map((r) => (
                  <Chip
                    key={r}
                    label={r}
                    size="small"
                    onDelete={() =>
                      setConfig({
                        ...config,
                        restrictions: config.restrictions.filter((x) => x !== r),
                      })
                    }
                  />
                ))}
              </Box>
            </Box>
            <TextField
              label="Fallback Message"
              value={config.fallback_message}
              onChange={(e) => setConfig({ ...config, fallback_message: e.target.value })}
              fullWidth
            />
          </Box>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isLoading}
            sx={{ mt: 3 }}
          >
            Save Settings
          </Button>
        </Box>
      )}

      {/* Tab 2: Share */}
      {tab === 2 && (
        <Box sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Share with guests
          </Typography>

          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 3 }}>
            <Typography variant="h3" fontWeight={700} letterSpacing={1}>
              {phoneNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SMS number
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                onClick={() => handleCopy(phoneNumber)}
              >
                {copied ? 'Copied!' : 'Copy Number'}
              </Button>
            </Box>
          </Paper>

          <Typography variant="subtitle2" gutterBottom>
            Suggested message to share with guests:
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}
          >
            <Typography variant="body2" fontStyle="italic">
              Have questions? Text {phoneNumber} and our {instance.config.persona_name} will help!
            </Typography>
          </Paper>
          <Button
            size="small"
            startIcon={<CopyIcon />}
            onClick={() =>
              handleCopy(
                `Have questions? Text ${phoneNumber} and our ${instance.config.persona_name} will help!`
              )
            }
          >
            Copy Message
          </Button>
        </Box>
      )}

      {/* Tab 3: Test */}
      {tab === 3 && (
        <Box sx={{ maxWidth: 600 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a test message to see how your concierge responds.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendTest()}
              placeholder="Ask something a guest might ask…"
            />
            <Button
              variant="contained"
              endIcon={testLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSendTest}
              disabled={testLoading || !testMessage.trim()}
            >
              Send
            </Button>
          </Box>
          {testResponse && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Response:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {testResponse}
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Concierge?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{instance.name}</strong>, release its phone
            number, and remove all uploaded documents. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteConciergeMutation.mutate()}
            disabled={deleteConciergeMutation.isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
